import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { capChips, type Chip } from '../lib/entities'
import './EntityChips.css'

/**
 * Phase E5: a group of people or ensembles, as chips you can tap.
 *
 * One component, used by the event sheet's fact list and by a place's "Artists
 * shown" block — not a new family. The pill is the sheet's own tag shape
 * (`.u-chip__face`'s radius and border, at the fact list's meta size), so a
 * row of chips reads as part of the `<dl>` it sits in rather than as a control
 * strip that arrived from somewhere else.
 *
 * **The pill is the filter; the ↗ is Wikipedia.** The brief asked for the
 * chip's name to be the link, which would put the phase's own action (tap to
 * filter Browse, K6) and an outbound link on the same pixels — the name would
 * leave the app and the 4px beside it would filter it. This is `EventRow`'s
 * shape instead, which the app already reads this way: the object presses,
 * and a small control inside it does the other thing. Both targets are their
 * own ≥32px box.
 *
 * `expanded` resets when the chips change, so opening a second sheet does not
 * inherit the first one's expanded group.
 */
export function EntityChips({ chips, onPick, className }: {
  chips: Chip[]
  onPick: (chip: Chip) => void
  className?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const key = chips.map(c => c.id).join(',')
  useEffect(() => { setExpanded(false) }, [key])

  if (chips.length === 0) return null
  const { shown, hidden } = capChips(chips, expanded)

  return (
    <div className={['entchips', className].filter(Boolean).join(' ')}>
      {shown.map(c => (
        <span key={`${c.kind}:${c.role}:${c.id}`} className={c.fact ? 'entchip entchip--fact' : 'entchip'}>
          <span className="entchip__pill">
            <button
              type="button"
              className="entchip__tap"
              /* Rule 5: the canonical name is what is drawn; the printed
                 spelling E2 moved into `aliases` is a tooltip and nothing
                 more. Absent where the two are the same, which is every
                 person Wikidata has not been asked about. */
              title={c.src ?? undefined}
              onClick={() => onPick(c)}
            >
              <span className="entchip__name">{c.name}</span>
            </button>
            {c.href && (
              <a
                className="entchip__wiki"
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${c.name} on Wikipedia`}
                title="Wikipedia"
              >
                <ExternalLink size={12} aria-hidden="true" />
              </a>
            )}
          </span>
          {c.fact && <span className="entchip__fact">{c.fact}</span>}
        </span>
      ))}
      {hidden > 0 && (
        <button type="button" className="entchip entchip--more" onClick={() => setExpanded(true)}>
          +{hidden} more
        </button>
      )}
    </div>
  )
}
